import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Dialog } from "@/components/ui/dialog";
import { Sheet } from "@/components/ui/sheet";
import { StatTile } from "@/components/ui/stat-tile";
import { Section } from "@/components/ui/section";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { useActiveCalls, useHangupCall, useTransferCall, useOriginateCall, useExtensions } from "@/hooks/use-calls";
import { useContexts } from "@/hooks/use-contexts";
import { useEndpoints } from "@/hooks/use-endpoints";
import type { OriginateCallRequest } from "@/types";
import { channelLabel, formatDuration, summarizeActiveCalls } from "@/lib/calls";
import { PhoneOff, ArrowRightLeft, PhoneCall, Phone } from "lucide-react";
import { toast } from "sonner";

function formatMs(ms: number | null | undefined): string {
  if (ms == null) return "-";
  return `${ms.toFixed(1)} ms`;
}

// Asterisk reports jitter and RTT in SECONDS. Rendering them straight through
// formatMs() printed 0.026s of jitter as "0.0 ms", hiding real degradation
// behind a healthy-looking zero. Convert explicitly at every call site.
function formatSecondsAsMs(seconds: number | null | undefined): string {
  if (seconds == null) return "-";
  return formatMs(seconds * 1000);
}

export default function ActiveCallsPage() {
  const { data: calls, isLoading } = useActiveCalls();
  const hangup = useHangupCall();
  const transfer = useTransferCall();
  const originate = useOriginateCall();
  const { data: extensions } = useExtensions();
  const { data: contexts } = useContexts();
  const { data: endpoints } = useEndpoints();
  const [transferChannel, setTransferChannel] = useState<string | null>(null);
  const [destination, setDestination] = useState("");
  const [showNewCall, setShowNewCall] = useState(false);
  const [callerType, setCallerType] = useState<"extension" | "endpoint">("extension");
  const [callerContext, setCallerContext] = useState("");
  const [callerExt, setCallerExt] = useState("");
  const [callerEndpoint, setCallerEndpoint] = useState("");
  const [destType, setDestType] = useState<"extension" | "trunk" | "sip_uri">("extension");
  const [destExt, setDestExt] = useState("");
  const [destTrunk, setDestTrunk] = useState("");
  const [destNumber, setDestNumber] = useState("");
  const [destUri, setDestUri] = useState("");
  const [callerName, setCallerName] = useState("");
  const [callerIdNum, setCallerIdNum] = useState("");
  const [destContext, setDestContext] = useState("");
  const [callTimeout, setCallTimeout] = useState(30);

  const sortedExtensions = useMemo(() =>
    (extensions ?? []).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [extensions],
  );

  const callList = useMemo(
    () => (Array.isArray(calls) ? calls : []),
    [calls]
  );

  // Grouping channels into calls lives in lib/calls so that Overview reports
  // the same number this page does — the two used to disagree (Overview counted
  // channels) and one call showed up as "2 active calls" there and "1" here.
  const stats = useMemo(() => summarizeActiveCalls(callList), [callList]);

  const handleHangup = (channel: string) => {
    hangup.mutate(channel, {
      onSuccess: () => toast.success("Call terminated"),
      onError: () => toast.error("Failed to hang up call"),
    });
  };

  const openTransfer = (channel: string) => {
    setTransferChannel(channel);
    setDestination("");
  };

  const handleTransfer = () => {
    if (!transferChannel) return;
    transfer.mutate(
      { channel: transferChannel, destination },
      {
        onSuccess: () => {
          toast.success("Call transferred");
          setTransferChannel(null);
        },
        onError: () => toast.error("Failed to transfer call"),
      }
    );
  };

  const handleNewCall = () => {
    const body: OriginateCallRequest = {
      caller_type: callerType,
      dest_type: destType,
      caller_name: callerName || undefined,
      timeout: callTimeout,
    };

    if (callerIdNum.trim()) body.caller_id_num = callerIdNum.trim();

    if (callerType === "extension") {
      body.caller_ext = callerExt;
      body.caller_context = callerContext || undefined;
    } else {
      body.caller_endpoint = callerEndpoint;
    }

    if (destType === "extension") {
      body.dest_ext = destExt;
      body.dest_context = destContext || callerContext || undefined;
    } else if (destType === "trunk") {
      body.dest_trunk = destTrunk;
      body.dest_number = destNumber;
    } else {
      body.dest_uri = destUri;
    }

    originate.mutate(body, {
      onSuccess: () => {
        toast.success("Originating call");
        setShowNewCall(false);
        setCallerExt("");
        setCallerEndpoint("");
        setDestExt("");
        setDestNumber("");
        setDestUri("");
        setCallerName("");
        setCallerIdNum("");
        setDestContext("");
        setCallTimeout(30);
      },
      onError: (err) => toast.error(`Failed: ${err.message}`),
    });
  };

  const canSubmit = (() => {
    if (callerType === "extension" && (!callerExt.trim() || !callerContext)) return false;
    if (callerType === "endpoint" && !callerEndpoint) return false;
    if (destType === "extension" && !destExt.trim()) return false;
    if (destType === "trunk" && (!destTrunk || !destNumber.trim())) return false;
    if (destType === "sip_uri" && !destUri.trim()) return false;
    return true;
  })();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Active Calls"
        description={
          <span className="inline-flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
            </span>
            Live, refreshes every 5 seconds
          </span>
        }
        actions={
          <Button onClick={() => setShowNewCall(true)} size="sm" className="gap-1.5">
            <PhoneCall size={14} />
            New Call
          </Button>
        }
      />

      <Section title="Traffic">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Active calls"
          value={stats.callCount}
          sub={
            stats.channelCount !== stats.callCount
              ? channelLabel(stats.channelCount)
              : undefined
          }
          info="Calls in progress, including those still ringing. One call uses several channels — dialling out through a trunk is a Local channel pair plus the outbound leg — so the channel count below is usually higher."
          tone={stats.callCount > 0 ? "success" : "default"}
          loading={isLoading}
          className="bg-background"
        />
        <StatTile
          label="States"
          value={`${stats.up} / ${stats.ringing}`}
          sub="up / ringing"
          info="Breakdown of calls (not channels): answered versus still ringing. A call counts as up once any of its legs is answered."
          loading={isLoading}
          className="bg-background"
        />
        <StatTile
          label="Avg RTT"
          value={stats.avgRtt != null ? formatMs(stats.avgRtt) : "-"}
          info="Mean RTP round-trip time across active channels. Above 150 ms callers will notice delay."
          tone={stats.avgRtt != null && stats.avgRtt > 150 ? "warning" : "default"}
          loading={isLoading}
          className="bg-background"
        />
        <StatTile
          label="Packets lost"
          value={stats.lost}
          info="Total RTP packets lost (receive + transmit) across all active channels. Non-zero values indicate network quality issues."
          tone={stats.lost > 0 ? "destructive" : "default"}
          loading={isLoading}
          className="bg-background"
        />
        </div>
      </Section>

      <Section title="Active channels">
      {isLoading ? (
        <div className="space-y-2 rounded-sm border border-border bg-background p-4">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : callList.length === 0 ? (
        <EmptyState
          icon={<Phone size={24} />}
          title="No active calls"
          description="Live calls appear here within 5 seconds of setup. Use New Call to originate one."
          className="rounded-sm border border-border bg-background"
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Channel</TableHead>
              <TableHead>Call ID</TableHead>
              <TableHead>Caller</TableHead>
              <TableHead>Connected</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Jitter RX</TableHead>
              <TableHead>Jitter TX</TableHead>
              <TableHead>Lost PKT</TableHead>
              <TableHead>RTT</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {callList.map((call) => (
              <TableRow key={call.unique_id}>
                <TableCell className="max-w-[200px] truncate font-mono text-xs">
                  {call.channel}
                </TableCell>
                <TableCell
                  className="max-w-[120px] truncate font-mono text-xs"
                  title={call.linked_id ?? ""}
                >
                  {call.linked_id || "-"}
                </TableCell>
                <TableCell>
                  <div>{call.caller_id_num || "-"}</div>
                  {call.caller_id_name && (
                    <div className="text-xs text-muted-foreground">
                      {call.caller_id_name}
                    </div>
                  )}
                </TableCell>
                <TableCell>{call.connected_line_num || "-"}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      call.state === "Up"
                        ? "success"
                        : call.state === "Ringing"
                          ? "warning"
                          : "neutral"
                    }
                  >
                    {call.state || "-"}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {formatDuration(call.duration_seconds)}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {formatSecondsAsMs(call.jitter_rx)}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {formatSecondsAsMs(call.jitter_tx)}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {call.packets_lost_rx != null
                    ? `${call.packets_lost_rx} / ${call.packets_lost_tx ?? "-"}`
                    : "-"}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {/* Null check, not truthiness: a real 0 ms RTT rendered as "-"
                      here while the Avg RTT tile averaged the same 0 to "0.0 ms". */}
                  {formatSecondsAsMs(call.rtt_seconds)}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleHangup(call.channel)}
                      disabled={hangup.isPending}
                      className="h-7 px-1.5 text-destructive-text hover:text-destructive-text hover:bg-destructive-subtle"
                      aria-label="Hang up"
                      title="Hang up"
                    >
                      <PhoneOff size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openTransfer(call.channel)}
                      className="h-7 px-1.5 text-primary hover:text-primary hover:bg-info-subtle"
                      aria-label="Transfer"
                      title="Transfer"
                    >
                      <ArrowRightLeft size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      </Section>

      <Dialog
        open={transferChannel !== null}
        onClose={() => setTransferChannel(null)}
        title="Transfer Call"
        className="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select an extension or enter extension@host or sip:uri to transfer
          </p>
          {sortedExtensions.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Configured Extensions</label>
              <Select
                value=""
                onChange={(e) => setDestination(e.target.value)}
              >
                <option value="">-- select extension --</option>
                {sortedExtensions.map((ext) => (
                  <option key={ext} value={ext}>{ext}</option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Custom Destination</label>
            <Input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="1001@endpoint or sip:user@provider.com"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setTransferChannel(null)}>Cancel</Button>
            <Button onClick={handleTransfer} loading={transfer.isPending} disabled={!destination.trim()}>
              Transfer
            </Button>
          </div>
        </div>
      </Dialog>

      <Sheet
        open={showNewCall}
        onClose={() => setShowNewCall(false)}
        title="New Call"
        description="Originate a call between a local source and any destination."
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowNewCall(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleNewCall}
              loading={originate.isPending}
              disabled={!canSubmit}
            >
              Call
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          <fieldset>
            <legend className="text-[13px] font-semibold mb-2">Caller Source</legend>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="callerType"
                  value="extension"
                  checked={callerType === "extension"}
                  onChange={() => setCallerType("extension")}
                />
                Internal extension
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="callerType"
                  value="endpoint"
                  checked={callerType === "endpoint"}
                  onChange={() => setCallerType("endpoint")}
                />
                PJSIP endpoint
              </label>
            </div>
            {callerType === "extension" ? (
              <div className="flex gap-2">
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Context</label>
                  {/* The placeholder used to read "default" with an empty value,
                      sitting next to a real context also named "default". Picking
                      it sent no context at all and the call failed downstream. */}
                  <Select
                    value={callerContext}
                    onChange={(e) => setCallerContext(e.target.value)}
                  >
                    <option value="" disabled>
                      — select context —
                    </option>
                    {(contexts ?? []).map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">
                    Extension to run
                  </label>
                  <Input
                    value={callerExt}
                    onChange={(e) => setCallerExt(e.target.value)}
                    placeholder="e.g. 1001"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    The dialplan extension executed on the calling leg.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Endpoint</label>
                <Select
                  value={callerEndpoint}
                  onChange={(e) => setCallerEndpoint(e.target.value)}
                >
                  <option value="">-- select endpoint --</option>
                  {(endpoints ?? []).map((ep) => (
                    <option key={ep.id} value={ep.name}>{ep.name}</option>
                  ))}
                </Select>
              </div>
            )}
          </fieldset>

          <div className="border-t border-border" />

          <fieldset>
            <legend className="text-[13px] font-semibold mb-2">Destination</legend>
            <div className="flex flex-wrap gap-4 mb-2">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="destType"
                  value="extension"
                  checked={destType === "extension"}
                  onChange={() => setDestType("extension")}
                />
                Internal extension
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="destType"
                  value="trunk"
                  checked={destType === "trunk"}
                  onChange={() => setDestType("trunk")}
                />
                External via trunk
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="destType"
                  value="sip_uri"
                  checked={destType === "sip_uri"}
                  onChange={() => setDestType("sip_uri")}
                />
                Direct SIP URI
              </label>
            </div>
            {destType === "extension" ? (
              <div className="flex gap-2">
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Context</label>
                  {/* Previously the destination silently reused the caller's
                      context, so one control meant two different things. */}
                  <Select
                    value={destContext || callerContext}
                    onChange={(e) => setDestContext(e.target.value)}
                  >
                    <option value="" disabled>
                      — select context —
                    </option>
                    {(contexts ?? []).map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Extension number</label>
                  <Input
                    value={destExt}
                    onChange={(e) => setDestExt(e.target.value)}
                    placeholder="e.g. 1002"
                  />
                </div>
              </div>
            ) : destType === "trunk" ? (
              <div className="flex gap-2">
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Endpoint (trunk)</label>
                  {/* This lists every PJSIP endpoint, not only trunks — the label
                      says so rather than implying a filter that does not exist. */}
                  <Select
                    value={destTrunk}
                    onChange={(e) => setDestTrunk(e.target.value)}
                  >
                    <option value="" disabled>
                      — select endpoint —
                    </option>
                    {(endpoints ?? []).map((ep) => (
                      <option key={ep.id} value={ep.name}>{ep.name}</option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Number</label>
                  <Input
                    value={destNumber}
                    onChange={(e) => setDestNumber(e.target.value)}
                    placeholder="e.g. +919876543210"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">SIP URI</label>
                <Input
                  value={destUri}
                  onChange={(e) => setDestUri(e.target.value)}
                  placeholder="sip:user@provider.com"
                />
              </div>
            )}
          </fieldset>

          <div className="border-t border-border" />

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              Caller ID number (optional)
            </label>
            <Input
              value={callerIdNum}
              onChange={(e) => setCallerIdNum(e.target.value)}
              placeholder="e.g. +16628762801"
            />
            <p className="text-[11px] text-muted-foreground">
              Presented to the far end. Leave blank to use the extension above —
              carriers reject a caller ID that is not a number you own. A trunk
              with <code>from_user</code> set overrides this.
            </p>
          </div>

          <div className="flex gap-2">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs text-muted-foreground">Caller name (optional)</label>
              <Input
                value={callerName}
                onChange={(e) => setCallerName(e.target.value)}
                placeholder="e.g. Alice"
              />
              <p className="text-[11px] text-muted-foreground">
                Display name only — it does not change the number.
              </p>
            </div>
            <div className="w-24 space-y-1.5">
              <label className="text-xs text-muted-foreground">Timeout (s)</label>
              <Input
                type="number"
                value={String(callTimeout)}
                onChange={(e) => setCallTimeout(Number(e.target.value) || 30)}
                min={1}
                max={120}
              />
            </div>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
